import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { userService } from '../services/userService';
import organizationService from '../services/organizationService';
import { projects as projectsApi } from '../services/api';
import logger from '../utils/logger';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Extract a projectId from URL paths that contain one.
 * Covers /projects/edit/:projectId, /projects/:projectId/*, and /roadmap/:projectId.
 * Returns null for non-UUID segments like 'edit' or 'new'.
 */
const getProjectIdFromUrl = (pathname) => {
  const editMatch = matchPath('/projects/edit/:projectId', pathname);
  if (editMatch?.params?.projectId && UUID_RE.test(editMatch.params.projectId)) {
    return editMatch.params.projectId;
  }
  const m =
    matchPath('/projects/:projectId/*', pathname) ||
    matchPath('/roadmap/:projectId', pathname);
  const id = m?.params?.projectId;
  return id && UUID_RE.test(id) ? id : null;
};

/**
 * Role hierarchy levels used for permission checks.
 */
const ROLE_LEVELS = {
  viewer: 10,
  contributor: 20,
  maintainer: 30,
  owner: 40,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AppDataContext = createContext({});

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AppDataProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const location = useLocation();

  // Raw data from the server
  const [startupData, setStartupData] = useState(null);

  // Derived UI state
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [projectPermissions, setProjectPermissions] = useState({});

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  // Guard against concurrent fetches — only reset on logout or explicit refresh
  const isLoadingRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Fetch startup context
  // ---------------------------------------------------------------------------

  const fetchStartupContext = useCallback(async () => {
    if (!user || isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      logger.debug('[AppDataContext] Fetching /users/me/context');
      const response = await api.get('/users/me/context');
      const data = response.data;
      setStartupData(data);
      setInitialized(true);
      return data;
    } catch (err) {
      logger.error('[AppDataContext] Error fetching startup context:', err);
      setError(err.message || 'Failed to load app data');
      return null;
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [user]);

  // ---------------------------------------------------------------------------
  // Derive state from startupData + currentOrganization
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!startupData) return;

    const orgs = startupData.organizations || [];
    setOrganizations(orgs);

    // Restore or pick the current org
    setCurrentOrganization((prev) => {
      if (prev) {
        // Keep current org if it still exists in the refreshed list
        const stillExists = orgs.find((o) => o.id === prev.id);
        if (stillExists) return stillExists;
      }

      const savedOrgId = localStorage.getItem('currentOrganizationId');
      if (savedOrgId) {
        const found = orgs.find((o) => o.id === savedOrgId);
        if (found) return found;
      }

      // Default to personal org, falling back to first org in list
      return orgs.find((o) => o.is_personal) || orgs[0] || null;
    });
  }, [startupData]);

  // When currentOrganization changes, update projects + permissions
  useEffect(() => {
    if (!startupData) {
      setProjects([]);
      setActiveProject(null);
      setProjectPermissions({});
      return;
    }

    // Determine the project list for the current org
    let activeProjects = [];
    let storageKey = 'activeProjectId';

    if (currentOrganization) {
      const orgData = (startupData.organizations || []).find(
        (o) => o.id === currentOrganization.id
      );
      activeProjects = orgData?.projects || [];
      storageKey = `activeProjectId:${currentOrganization.id}`;
    }

    setProjects(activeProjects);

    // Build permissions map
    const perms = {};
    activeProjects.forEach((p) => {
      perms[p.id] = {
        role: p.role,
        hasAccess: true,
        projectName: p.name,
        orgId: p.org_id,
      };
    });
    setProjectPermissions(perms);

    // Choose the active project
    const urlProjectId = getProjectIdFromUrl(location.pathname);
    const lastProjectId = startupData.last_project_id;
    const storedProjectId =
      localStorage.getItem(storageKey) || localStorage.getItem('activeProjectId');

    let chosen = null;
    if (urlProjectId && activeProjects.some((p) => p.id === urlProjectId)) {
      chosen = activeProjects.find((p) => p.id === urlProjectId);
      logger.debug(`[AppDataContext] Active project from URL: ${urlProjectId}`);
    } else if (lastProjectId && activeProjects.some((p) => p.id === lastProjectId)) {
      chosen = activeProjects.find((p) => p.id === lastProjectId);
      logger.debug(`[AppDataContext] Active project from last_project_id: ${lastProjectId}`);
    } else if (storedProjectId && activeProjects.some((p) => p.id === storedProjectId)) {
      chosen = activeProjects.find((p) => p.id === storedProjectId);
      logger.debug(`[AppDataContext] Active project from localStorage: ${storedProjectId}`);
    } else {
      chosen = activeProjects[0] || null;
      logger.debug(`[AppDataContext] Active project defaulted to first: ${chosen?.id}`);
    }

    setActiveProject(chosen);
    if (chosen) {
      localStorage.setItem(storageKey, chosen.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization, startupData]);

  // ---------------------------------------------------------------------------
  // Persist currentOrganization choice
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (currentOrganization) {
      localStorage.setItem('currentOrganizationId', currentOrganization.id);
    }
  }, [currentOrganization]);

  // ---------------------------------------------------------------------------
  // Auth-driven fetch
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      // Use isLoadingRef to prevent StrictMode double-invocation and tab-switch
      // token-refresh from triggering duplicate fetches. The ref is NOT reset here —
      // only reset inside fetchStartupContext (on completion) or on logout below.
      fetchStartupContext();
    } else if (!authLoading && !isAuthenticated) {
      setStartupData(null);
      setOrganizations([]);
      setCurrentOrganization(null);
      setProjects([]);
      setActiveProject(null);
      setProjectPermissions({});
      setInitialized(false);
      isLoadingRef.current = false;
    }
    // Use user?.id instead of user to avoid re-firing on object reference changes
    // (e.g. Supabase TOKEN_REFRESHED events on tab focus).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, authLoading]);

  // ---------------------------------------------------------------------------
  // Public API — org actions
  // ---------------------------------------------------------------------------

  /** Force a full refresh from the server */
  const refreshOrganizations = useCallback(async () => {
    isLoadingRef.current = false;
    await fetchStartupContext();
  }, [fetchStartupContext]);

  const switchOrganization = useCallback((org) => {
    setCurrentOrganization(org);
    if (org) {
      localStorage.setItem('currentOrganizationId', org.id);
    } else {
      localStorage.removeItem('currentOrganizationId');
    }
    // Projects + permissions will be updated by the currentOrganization useEffect above
  }, []);

  const createOrganization = useCallback(
    async (organizationData) => {
      setLoading(true);
      setError(null);
      try {
        const newOrg = await organizationService.createOrganization(organizationData);
        await refreshOrganizations();
        // The raw API response lacks a role field. The creator is always owner,
        // so inject it so currentOrganization has the correct role immediately.
        switchOrganization({ ...newOrg, role: 'owner' });
        return newOrg;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refreshOrganizations, switchOrganization]
  );

  const updateOrganization = useCallback(
    async (orgId, organizationData) => {
      setLoading(true);
      setError(null);
      try {
        const updatedOrg = await organizationService.updateOrganization(orgId, organizationData);
        await refreshOrganizations();
        return updatedOrg;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refreshOrganizations]
  );

  const deleteOrganization = useCallback(
    async (orgId) => {
      setLoading(true);
      setError(null);
      try {
        await organizationService.deleteOrganization(orgId);
        await refreshOrganizations();
        return true;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refreshOrganizations]
  );

  // ---------------------------------------------------------------------------
  // Public API — org role helpers
  // ---------------------------------------------------------------------------

  const getCurrentUserRole = useCallback(() => {
    return currentOrganization?.role || null;
  }, [currentOrganization]);

  const hasRole = useCallback(
    (requiredRoles) => {
      const userRole = getCurrentUserRole();
      if (!userRole) return false;
      const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
      return roles.includes(userRole);
    },
    [getCurrentUserRole]
  );

  const canManageOrganization = useCallback(
    () => hasRole(['owner', 'admin']),
    [hasRole]
  );

  const isOrganizationOwner = useCallback(
    () => hasRole(['owner']),
    [hasRole]
  );

  // ---------------------------------------------------------------------------
  // Public API — project actions
  // ---------------------------------------------------------------------------

  const selectProject = useCallback(
    (project) => {
      setActiveProject(project);
      if (project) {
        const storageKey = currentOrganization
          ? `activeProjectId:${currentOrganization.id}`
          : 'activeProjectId';
        localStorage.setItem(storageKey, project.id);

        // Debounced server update
        if (window.lastProjectTimeout) {
          clearTimeout(window.lastProjectTimeout);
        }
        window.lastProjectTimeout = setTimeout(() => {
          userService.updateLastProject(project.id).catch(console.error);
        }, 1000);
      } else {
        if (currentOrganization) {
          localStorage.removeItem(`activeProjectId:${currentOrganization.id}`);
        }
        localStorage.removeItem('activeProjectId');
      }
    },
    [currentOrganization]
  );

  // Sync active project to URL — runs on every navigation after selectProject is defined
  useEffect(() => {
    if (!projects.length) return;
    const urlProjectId = getProjectIdFromUrl(location.pathname);
    if (!urlProjectId) return;                        // not a project page
    if (activeProject?.id === urlProjectId) return;  // already correct
    const match = projects.find((p) => p.id === urlProjectId);
    if (match) {
      selectProject(match);
    }
  }, [location.pathname, projects, activeProject, selectProject]);

  const createProject = useCallback(
    async (projectData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await projectsApi.create(projectData);
        if (response.data) {
          await refreshOrganizations();
          return response.data;
        }
      } catch (err) {
        if (err.data?.detail?.error === 'usage_limit_exceeded') {
          const detail = err.data.detail;
          const msg = `Organization limit reached: Your organization has ${detail.current_usage} of ${detail.limit} ${detail.metric_type} allowed on the ${detail.plan} plan.`;
          setError(msg);
        } else {
          setError(err.message || 'Failed to create project');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refreshOrganizations]
  );

  const updateProject = useCallback(
    async (id, updates) => {
      setLoading(true);
      setError(null);
      try {
        const response = await projectsApi.update(id, updates);
        if (response.data) {
          await refreshOrganizations();
          return response.data;
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refreshOrganizations]
  );

  const deleteProject = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await projectsApi.delete(id);
        if (activeProject && activeProject.id === id) {
          selectProject(null);
        }
        await refreshOrganizations();
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [activeProject, refreshOrganizations, selectProject]
  );

  // ---------------------------------------------------------------------------
  // Public API — project permission helpers
  // ---------------------------------------------------------------------------

  const hasProjectPermission = useCallback(
    (projectId, requiredRole = 'viewer') => {
      const perm = projectPermissions[projectId];
      if (!perm) return false;
      const userLevel = ROLE_LEVELS[perm.role] || 0;
      const requiredLevel = ROLE_LEVELS[requiredRole] || 0;
      return userLevel >= requiredLevel;
    },
    [projectPermissions]
  );

  const getProjectRole = useCallback(
    (projectId) => {
      return projectPermissions[projectId]?.role || null;
    },
    [projectPermissions]
  );

  const hasProjectAccess = useCallback(
    (projectId) => {
      return projectPermissions[projectId]?.hasAccess === true;
    },
    [projectPermissions]
  );

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value = {
    // Raw startup data (rarely needed by consumers)
    startupData,

    // Org state
    organizations,
    currentOrganization,
    loading,
    initialized,
    error,

    // Org actions
    refreshOrganizations,
    switchOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,

    // Org role helpers
    getCurrentUserRole,
    hasRole,
    canManageOrganization,
    isOrganizationOwner,

    // Project state
    projects,
    activeProject,

    // Project actions
    selectProject,
    createProject,
    updateProject,
    deleteProject,

    // Project permissions
    projectPermissions,
    hasProjectPermission,
    getProjectRole,
    hasProjectAccess,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export default AppDataContext;
